/**
 * Rent cycles.
 *
 * This is the file that separates RentorNG from software written for monthly rent. A cycle is
 * not "monthly with a multiplier" — an annual tenancy is one payment of one year's rent, due
 * once, in advance, and the whole billing schedule follows from that. Getting this wrong means
 * generating twelve invoices for a tenant who paid once, which is worse than generating none.
 */

import { addDays, addMonths, diffDays, inclusiveDays, type PlainDate } from "./dates";

export type RentCycle =
  | "monthly"
  | "quarterly"
  | "biannual"
  | "annual"
  | "biennial"
  | "weekly"
  | "one_off";

export interface CycleInfo {
  value: RentCycle;
  label: string;
  /** Length in months, where that is meaningful. `null` for weekly and one-off. */
  months: number | null;
  /** Roughly how many of these fall in a year — used only for annualising, never for billing. */
  perYear: number;
  /** Cycles we present first in a dropdown, because they are what actually gets used. */
  common: boolean;
}

export const RENT_CYCLES: CycleInfo[] = [
  { value: "annual", label: "Annual", months: 12, perYear: 1, common: true },
  { value: "biennial", label: "Every 2 years", months: 24, perYear: 0.5, common: true },
  { value: "biannual", label: "Every 6 months", months: 6, perYear: 2, common: true },
  { value: "quarterly", label: "Quarterly", months: 3, perYear: 4, common: true },
  { value: "monthly", label: "Monthly", months: 1, perYear: 12, common: true },
  { value: "weekly", label: "Weekly", months: null, perYear: 52, common: false },
  { value: "one_off", label: "One-off", months: null, perYear: 1, common: false },
];

const BY_VALUE = new Map(RENT_CYCLES.map((c) => [c.value, c]));

export function cycleInfo(cycle: RentCycle): CycleInfo {
  const info = BY_VALUE.get(cycle);
  if (!info) throw new Error(`Unknown rent cycle: ${cycle}`);
  return info;
}

export function cycleLabel(cycle: RentCycle): string {
  return cycleInfo(cycle).label;
}

/**
 * A term: how often the rent falls due.
 *
 * The period says what kind of interval, the count says how many of them — "every 3 years",
 * "every 2 weeks". One is meaningless without the other, so they travel together and are never
 * read apart.
 *
 * A count of 1 is the ordinary case and what every record meant before the count existed, which
 * is why adding it needed no data migration.
 */
export interface RentTerm {
  cycle: RentCycle;
  /** At least 1. A one-off ignores it. */
  every: number;
}

/** The units a term can be built from. Anything longer is expressed as a count of these. */
export const CYCLE_UNITS: RentCycle[] = ["weekly", "monthly", "quarterly", "annual"];

/** Singular and plural names for a period, for building phrases like "every 3 years". */
const PERIOD_WORDS: Record<RentCycle, [string, string]> = {
  weekly: ["week", "weeks"],
  monthly: ["month", "months"],
  quarterly: ["quarter", "quarters"],
  biannual: ["6 months", "lots of 6 months"],
  annual: ["year", "years"],
  biennial: ["2 years", "lots of 2 years"],
  one_off: ["one-off", "one-off"],
};

/** Normalises anything that arrives from a form or an old record. */
export function toTerm(cycle: RentCycle, every: number | null | undefined): RentTerm {
  const count = Math.max(1, Math.floor(Number(every) || 1));
  return { cycle, every: cycle === "one_off" ? 1 : count };
}

/** "Yearly", "Every 3 years", "One-off" — for a heading or a dropdown. */
export function termLabel(term: RentTerm): string {
  if (term.cycle === "one_off") return "One-off";
  const [one, many] = PERIOD_WORDS[term.cycle];
  if (term.every === 1) return cycleLabel(term.cycle);
  return `Every ${term.every} ${many}`;
}

/** "per year", "per 3 years" — the suffix beside a rent figure. */
export function termSuffix(term: RentTerm): string {
  if (term.cycle === "one_off") return "one-off";
  if (term.every === 1) return cycleSuffix(term.cycle);
  const [, many] = PERIOD_WORDS[term.cycle];
  return `per ${term.every} ${many}`;
}

/** How many of these terms fall in a year. Used only for annualising, never for billing. */
export function termsPerYear(term: RentTerm): number {
  return cycleInfo(term.cycle).perYear / Math.max(1, term.every);
}

/** "per year", "per month" — the suffix shown beside a rent figure. */
export function cycleSuffix(cycle: RentCycle): string {
  switch (cycle) {
    case "annual":
      return "per year";
    case "biennial":
      return "per 2 years";
    case "biannual":
      return "per 6 months";
    case "quarterly":
      return "per quarter";
    case "monthly":
      return "per month";
    case "weekly":
      return "per week";
    case "one_off":
      return "one-off";
  }
}

/** Advance a date by one cycle. The engine of every schedule in the product. */
export function advance(date: PlainDate, cycle: RentCycle, count = 1): PlainDate {
  const info = cycleInfo(cycle);
  if (info.months !== null) return addMonths(date, info.months * count);
  if (cycle === "weekly") return addDays(date, 7 * count);
  return date; // one-off never advances
}

export interface CyclePeriod {
  index: number;
  /** First day covered by the payment. */
  start: PlainDate;
  /** Last day covered — inclusive. A twelve-month period ending 28 Feb ends *on* 28 Feb. */
  end: PlainDate;
  /** The day the money is expected. */
  dueDate: PlainDate;
  /** Days actually covered, which matters only for a pro-rated first or last period. */
  days: number;
  /** True when the period was cut short by the lease's end date. */
  partial: boolean;
}

export interface PeriodOptions {
  start: PlainDate;
  /** Inclusive last day of the tenancy. Omit for an open-ended lease. */
  end?: PlainDate;
  cycle: RentCycle;
  /** How many cycles make one billing period. 1 unless the term says otherwise. */
  every?: number;
  /** In advance is the norm here; in arrears exists for commercial lettings. */
  terms?: "advance" | "arrears";
  /** Safety valve so an open-ended monthly lease cannot generate an unbounded array. */
  maxPeriods?: number;
}

/**
 * Expand a lease into its billing periods.
 *
 * A period runs from its start to the day before the next period begins — so a lease starting
 * 1 March on an annual cycle produces 1 Mar 2026 to 28 Feb 2027, and the next begins 1 Mar
 * 2027. There is never a gap and never an overlap, which is what makes arrears arithmetic
 * trustworthy.
 */
export function periodsFor(opts: PeriodOptions): CyclePeriod[] {
  const { start, end, cycle, terms = "advance", maxPeriods = 240 } = opts;
  const every = Math.max(1, Math.floor(opts.every ?? 1));
  if (end && end < start) throw new Error("A lease cannot end before it starts");

  if (cycle === "one_off") {
    const last = end ?? start;
    return [
      {
        index: 0,
        start,
        end: last,
        dueDate: terms === "advance" ? start : last,
        days: inclusiveDays(start, last),
        partial: false,
      },
    ];
  }

  const out: CyclePeriod[] = [];
  let cursor = start;
  let index = 0;

  while (index < maxPeriods) {
    const nextStart = advance(start, cycle, (index + 1) * every);
    const naturalEnd = addDays(nextStart, -1);
    const partial = Boolean(end && end < naturalEnd);
    const periodEnd = end && end < naturalEnd ? end : naturalEnd;

    out.push({
      index,
      start: cursor,
      end: periodEnd,
      dueDate: terms === "advance" ? cursor : periodEnd,
      days: inclusiveDays(cursor, periodEnd),
      partial,
    });

    if (end && periodEnd >= end) break;
    if (!end && out.length >= 1 && !(every === 1 && (cycle === "monthly" || cycle === "weekly"))) {
      // An open-ended annual lease bills one year at a time; the next is created on renewal.
      break;
    }
    if (!end && out.length >= 12) break; // open-ended monthly: a year ahead is enough

    cursor = nextStart;
    index += 1;
  }

  return out;
}

/**
 * The natural end date of a lease of `count` billing periods starting on `start` (inclusive).
 *
 * `every` is the length of one period, so a two-period lease on a term of "every 6 months" runs
 * a year — not six months, which is what dropping the multiplier here would silently produce.
 */
export function leaseEndFor(start: PlainDate, cycle: RentCycle, count = 1, every = 1): PlainDate {
  if (cycle === "one_off") return start;
  return addDays(advance(start, cycle, count * Math.max(1, every)), -1);
}

/** How many whole cycles fit between two dates. Used when a manager types dates rather than a term. */
export function cycleCountBetween(start: PlainDate, end: PlainDate, cycle: RentCycle): number {
  if (cycle === "one_off") return 1;
  let count = 0;
  while (count < 240 && leaseEndFor(start, cycle, count + 1) <= end) count += 1;
  return Math.max(1, count);
}

/**
 * Annualised rent, for comparing a shop on a quarterly cycle with a flat on a two-year one.
 * Reporting only — never used to bill.
 */
export function annualisedRent(amount: number, cycle: RentCycle): number {
  const { perYear } = cycleInfo(cycle);
  return Math.round(amount * perYear);
}

/** Which period a given day falls into, or null if the day is outside the lease. */
export function periodContaining(periods: CyclePeriod[], date: PlainDate): CyclePeriod | null {
  return periods.find((p) => date >= p.start && date <= p.end) ?? null;
}

/** The next period whose money has not yet fallen due, relative to `asOf`. */
export function nextDuePeriod(periods: CyclePeriod[], asOf: PlainDate): CyclePeriod | null {
  return periods.find((p) => p.dueDate >= asOf) ?? null;
}

/** Days until a lease ends. Negative once it has expired. The dashboard's expiry bucket key. */
export function daysToExpiry(end: PlainDate, asOf: PlainDate): number {
  return diffDays(asOf, end);
}
